import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { comparisonAPI, formatApiError } from '@/lib/api';
import { Eye, ArrowLeft, Upload, Loader2, Settings, LogOut, GitCompare, FileText, Lock } from 'lucide-react';
import { toast } from 'sonner';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-body text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
          <Link to="/settings" className="p-2 hover:bg-zinc-100 transition-colors"><Settings className="w-4 h-4 text-zinc-600" /></Link>
          <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 hover:bg-zinc-100 transition-colors"><LogOut className="w-4 h-4 text-zinc-600" /></button>
        </div>
      </div>
    </header>
  );
}

function ContractInput({ side, title, setTitle, text, setText, file, setFile, inputMode, setInputMode }) {
  const fileRef = useRef(null);
  const sideLabel = side === 'original' ? 'Original' : 'Revised';
  const sideColor = side === 'original' ? 'border-t-zinc-400' : 'border-t-brand';

  return (
    <div className={`bg-white border border-zinc-200 border-t-2 ${sideColor}`} data-testid={`compare-${side}-panel`}>
      <div className="px-6 py-4 border-b border-zinc-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-sm text-zinc-900">{sideLabel} Contract</h3>
          <div className="flex gap-2">
            <button onClick={() => setInputMode('paste')} className={`px-3 py-1 text-xs font-body font-medium transition-colors ${inputMode === 'paste' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`} data-testid={`compare-${side}-paste-tab`}>Paste</button>
            <button onClick={() => setInputMode('file')} className={`px-3 py-1 text-xs font-body font-medium transition-colors ${inputMode === 'file' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`} data-testid={`compare-${side}-file-tab`}>Upload</button>
          </div>
        </div>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={`${sideLabel} title (optional)`}
          className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand px-3 py-1.5 font-body text-xs outline-none transition-colors"
          data-testid={`compare-${side}-title`}
        />
      </div>
      <div className="p-6">
        {inputMode === 'paste' ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Paste the ${sideLabel.toLowerCase()} contract text here...`}
            rows={12}
            className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand px-4 py-3 font-body text-sm outline-none transition-colors resize-y"
            data-testid={`compare-${side}-text`}
          />
        ) : (
          <div
            className="border-2 border-dashed border-zinc-200 p-8 flex flex-col items-center justify-center cursor-pointer hover:border-brand/50 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid={`compare-${side}-dropzone`}
          >
            {file ? (
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-brand" />
                <span className="font-body text-sm text-zinc-700">{file.name}</span>
                <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-xs text-zinc-400 hover:text-rose-500 ml-2">Remove</button>
              </div>
            ) : (
              <>
                <Upload className="w-6 h-6 text-zinc-400 mb-2" strokeWidth={1.5} />
                <p className="font-body text-xs text-zinc-500">Click to upload PDF or DOCX</p>
              </>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ComparePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [origTitle, setOrigTitle] = useState('');
  const [origText, setOrigText] = useState('');
  const [origFile, setOrigFile] = useState(null);
  const [origMode, setOrigMode] = useState('paste');

  const [revTitle, setRevTitle] = useState('');
  const [revText, setRevText] = useState('');
  const [revFile, setRevFile] = useState(null);
  const [revMode, setRevMode] = useState('paste');

  const [loading, setLoading] = useState(false);

  const isProPlus = user?.plan_type === 'pro_plus' || user?.role === 'admin';

  const handleCompare = async () => {
    const formData = new FormData();
    formData.append('original_title', origTitle || 'Original Contract');
    formData.append('revised_title', revTitle || 'Revised Contract');

    if (origMode === 'file' && origFile) {
      formData.append('original_file', origFile);
    } else if (origText.trim().length >= 50) {
      formData.append('original_text', origText);
    } else {
      toast.error('Original contract needs at least 50 characters of text.');
      return;
    }

    if (revMode === 'file' && revFile) {
      formData.append('revised_file', revFile);
    } else if (revText.trim().length >= 50) {
      formData.append('revised_text', revText);
    } else {
      toast.error('Revised contract needs at least 50 characters of text.');
      return;
    }

    setLoading(true);
    try {
      const res = await comparisonAPI.compare(formData);
      toast.success('Comparison complete!');
      navigate(`/comparison/${res.data.comparison_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="compare-page">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-1 font-body text-sm text-zinc-500 hover:text-zinc-700 mb-6" data-testid="compare-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <GitCompare className="w-6 h-6 text-brand" strokeWidth={1.5} />
              <h1 className="font-heading font-bold text-2xl tracking-tight text-zinc-900">Compare Contracts</h1>
            </div>
            <p className="font-body text-sm text-zinc-500">Upload or paste two versions of a contract to see how risk changed between versions. Uses 2 scans.</p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4" data-testid="compare-loading">
              <Loader2 className="w-10 h-10 text-brand animate-spin" />
              <p className="font-body text-sm text-zinc-600">Analyzing both contracts... This may take up to 2 minutes.</p>
              <p className="font-body text-xs text-zinc-400">We're running AI analysis on each version and generating a diff.</p>
            </div>
          ) : !isProPlus ? (
            <div className="bg-white border border-zinc-200 p-12 flex flex-col items-center justify-center text-center" data-testid="compare-upgrade-prompt">
              <Lock className="w-10 h-10 text-zinc-300 mb-4" />
              <h3 className="font-heading font-bold text-lg text-zinc-900 mb-2">Pro Plus Feature</h3>
              <p className="font-body text-sm text-zinc-500 mb-6 max-w-md">Contract comparison is available on the Pro Plus plan ($15/mo). Compare original and revised contracts to track how negotiations improve your terms.</p>
              <button onClick={() => navigate('/settings')} className="bg-brand text-white hover:bg-brand-hover px-8 py-3 font-body font-medium text-sm transition-colors" data-testid="compare-upgrade-btn">Upgrade to Pro Plus</button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <ContractInput side="original" title={origTitle} setTitle={setOrigTitle} text={origText} setText={setOrigText} file={origFile} setFile={setOrigFile} inputMode={origMode} setInputMode={setOrigMode} />
                <ContractInput side="revised" title={revTitle} setTitle={setRevTitle} text={revText} setText={setRevText} file={revFile} setFile={setRevFile} inputMode={revMode} setInputMode={setRevMode} />
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleCompare}
                  className="bg-brand text-white hover:bg-brand-hover px-10 py-3 font-body font-medium text-sm transition-colors flex items-center gap-2"
                  data-testid="compare-submit-btn"
                >
                  <GitCompare className="w-4 h-4" /> Compare Contracts
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-200 px-4 py-3 mt-6" data-testid="compare-disclaimer">
                <p className="font-body text-xs text-amber-800">This tool provides risk insights only and does not constitute legal advice.</p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
